import { logError } from 'services/logging/logging';
import { useGetRelationshipUploadDocumentUrl } from 'services/repositories/relationships/relationships';
import { generateTimestampedFilename, uploadFileToS3WithPresignedUrl } from 'support/helpers/fileUpload/fileUpload';
import { DocumentType } from 'support/types';
import { FileInputErrorCodes } from 'components/Form/FileInput/FileInput';

import { FileUploadError, TemplateValidationErrorCodes, UploadValidationError } from './helpers';

export type UseErrorHandlerProps = {
  relationshipId: string;
  setUploadError: (error: FileUploadError | undefined) => void;
  track: (event: string, properties?: Record<string, unknown>) => void;
};

export const useErrorHandler = ({ relationshipId, setUploadError, track }: UseErrorHandlerProps) => {
  const { mutate: getFileUploadUrl } = useGetRelationshipUploadDocumentUrl();

  const handleOnError = (error: UploadValidationError | Error, file: File) => {
    const timestampedFilename = generateTimestampedFilename(file.name);

    getFileUploadUrl(
      {
        relationshipId,
        payload: {
          file_name: timestampedFilename,
          message_type: DocumentType.productCatalog,
        },
      },
      {
        onSuccess: (data) => {
          if (!data?.url) {
            logError('No URL returned from getFileUploadUrl');
            return;
          }

          uploadFileToS3WithPresignedUrl(file, data.url).catch(logError);
        },
        onError: (error: unknown) => {
          logError(error);
        },
      },
    );

    track('webedi:pricat_uploaded_template.failed', {
      error: error.message,
      filename: file.name,
    });

    if (
      Object.values(FileInputErrorCodes).includes(error.message as FileInputErrorCodes) ||
      Object.values(TemplateValidationErrorCodes).includes(error.message as TemplateValidationErrorCodes)
    ) {
      if (error instanceof UploadValidationError) {
        setUploadError({ code: error.code, columns: error.columns || [], errorDetails: error.errorDetails });
      } else {
        setUploadError({
          code: error.message as FileInputErrorCodes,
        });
      }
    } else {
      logError(error);
      setUploadError({ code: 'UNKNOWN' });
    }
  };

  return { handleOnError };
};
